import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ChartComponent,
} from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '@ose/services';

type DonutChartItem = Record<string, unknown>;

@Component({
  selector: 'lib-donut2-chart',
  standalone: true,
  imports: [CommonModule, ChartComponent],
  templateUrl: './donut2-chart.component.html',
  styleUrls: ['./donut2-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class Donut2ChartComponent
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  @Input() data: DonutChartItem[] = [];

  @Input() labelKey = 'SIDEBAR.AssetStatistics';

  @Input() valueKey = 'rank';

  @Input() labelField = 'instrument_symbol';

  @Input()
  tooltipTemplateFn?: (item: DonutChartItem) => string;

  @Input()
  colorFn?: (item: DonutChartItem, index: number) => string;

  @Input() customCenterKey?: string;

  /*
   * جدید
   * اگر پاس داده شود، وسط دونات همین HTML نمایش داده می‌شود.
   * اگر پاس داده نشود، رفتار قبلی حفظ می‌شود.
   */
  @Input() customCenterHtml?: string;

  @Input() disableCenterLabelOnHover = false;

  ready = false;

  chart: ApexChart = {
    type: 'donut',
    animations: {
      enabled: false,
    },
  };

  dataLabels: ApexDataLabels = {
    enabled: false,
  };

  stroke: ApexStroke = {
    show: true,
    width: 0,
    lineCap: 'round',
  };

  opt: ApexPlotOptions = {
    pie: {
      donut: {
        size: '85%',
        labels: {
          show: true,
          value: {
            show: false,
          },
          total: {
            show: true,
            label: '',
            fontSize: '18px',
            color: 'var(--dark)',
            fontFamily: 'inherit',
            formatter: () => '',
          },
        },
      },
    },
  };

  toolTip: ApexTooltip = {
    enabled: true,
    custom: ({ seriesIndex }) => {
      const item = this.data[seriesIndex];

      if (!item) {
        return '';
      }

      if (this.tooltipTemplateFn) {
        return this.tooltipTemplateFn(item);
      }

      const label = String(item[this.labelField] ?? '');

      return `
        <div class="donut2-chart-tooltip">
          ${label}
        </div>
      `;
    },
  };

  series: number[] = [];
  labels: string[] = [];
  colors: string[] = [];

  readonly opacities: number[] = [1, 0.75, 0.5, 0.25];

  private readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly subscriptions: Subscription[] = [];

  ngOnInit(): void {
    const subscription = this.themeService.getThemeMode().subscribe(() => {
      this.setColors();
    });

    this.subscriptions.push(subscription);
  }

  ngAfterViewInit(): void {
    this.setColors();

    setTimeout(() => {
      this.ready = true;
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(): void {
    this.setColors();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  setColors(): void {
    this.series = [];
    this.labels = [];
    this.colors = [];

    const root = getComputedStyle(document.documentElement);
    const primary = root.getPropertyValue('--primary-rgb').trim();

    if (this.data.length === 0) {
      this.series = [100];
      this.colors = [`rgba(${primary}, 0.25)`];
      this.labels = [''];
      this.toolTip = {
        ...this.toolTip,
        enabled: false,
      };
      return;
    }

    if (this.disableCenterLabelOnHover) {
      this.opt = {
        pie: {
          donut: {
            size: '85%',
            labels: {
              show: true,
              name: {
                show: false,
              },
              value: {
                show: false,
              },
              total: {
                show: true,
                label: this.customCenterHtml
                  ? ''
                  : String(
                      this.translateService.instant(
                        this.customCenterKey ?? this.labelKey
                      )
                    ),
                fontSize: '18px',
                color: 'var(--dark)',
                fontFamily: 'inherit',
                formatter: () => '',
              },
            },
          },
        },
      };
    } else {
      this.opt = {
        pie: {
          donut: {
            size: '85%',
            labels: {
              show: true,
              name: {
                show: true,
              },
              value: {
                show: true,
              },
              total: {
                show: false,
              },
            },
          },
        },
      };
    }

    this.data.forEach((item: DonutChartItem, i: number) => {
      const rawValue = item[this.valueKey];
      const value = this.toNumber(rawValue, 1);
      const label = String(item[this.labelField] ?? '');
      const opacity = this.opacities[i % this.opacities.length] ?? 1;

      const color = this.colorFn
        ? this.colorFn(item, i)
        : `rgba(${primary}, ${opacity})`;

      this.series.push(value);
      this.labels.push(label);
      this.colors.push(color);
    });

    this.toolTip = {
      ...this.toolTip,
      enabled: true,
    };
  }

  private toNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return fallback;
  }

  convertNewlinesToBr(text: string | undefined): string {
    if (!text) {
      return '';
    }

    return text.replace(/\r?\n/g, '<br>');
  }

  get centerHtmlSafe(): string {
    if (this.customCenterHtml?.trim()) {
      return this.customCenterHtml;
    }

    const key = this.customCenterKey ?? this.labelKey;
    const translated = String(this.translateService.instant(key));

    return this.convertNewlinesToBr(translated);
  }
}
